import { useCallback, useEffect, useState } from 'react';
import type { SearchFormValues } from '@/features/tien-mat/hach-toan/phieu-chi/schema';
import type { PhieuChi, PhieuChiListResponse } from '@/types/schemas';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

export interface UsePhieuChiReturn {
  phieuChis: PhieuChi[];
  isLoading: boolean;
  error: string | null;
  fetchPhieuChis: () => Promise<void>;
  searchPhieuChis: (searchParams: SearchFormValues) => Promise<void>;
  createPhieuChi: (phieuChi: Partial<PhieuChi>) => Promise<PhieuChi>;
  updatePhieuChi: (uuid: string, phieuChi: Partial<PhieuChi>) => Promise<PhieuChi>;
  deletePhieuChi: (uuid: string) => Promise<boolean>;
  getPhieuChiByUuid: (uuid: string) => Promise<PhieuChi | null>;
  refreshPhieuChis: () => Promise<void>;
}

interface FilterResponse {
  results: PhieuChi[];
  count: number;
}

interface UseFilterPhieuChiReturn {
  filterPhieuChis: (filters: Record<string, any>, page?: number, pageSize?: number) => Promise<FilterResponse>;
}

/**
 * Hook for filtering PhieuChi without auto-fetch
 */
export const useFilterPhieuChi = (): UseFilterPhieuChiReturn => {
  const { entity } = useAuth();
  const API_ENDPOINT = 'tien-mat/hach-toan/phieu-chi';

  const filterPhieuChis = useCallback(
    async (filters: Record<string, any>, page: number = 0, pageSize: number = 30): Promise<FilterResponse> => {
      if (!entity?.slug) return { results: [], count: 0 };

      try {
        const response = await api.post(`/entities/${entity.slug}/erp/${API_ENDPOINT}/filter/`, filters, {
          params: {
            page: page + 1,
            page_size: pageSize
          }
        });

        if (Array.isArray(response.data)) {
          return { results: response.data, count: response.data.length };
        }

        if (response.data?.results) {
          return {
            results: response.data.results,
            count: response.data.count || response.data.results.length
          };
        }

        return { results: [], count: 0 };
      } catch (error) {
        console.error('Error filtering payment vouchers via filter API:', error);
        return { results: [], count: 0 };
      }
    },
    [entity?.slug]
  );

  return { filterPhieuChis };
};

export const usePhieuChi = (initialPhieuChis: PhieuChi[] = []): UsePhieuChiReturn => {
  const [phieuChis, setPhieuChis] = useState<PhieuChi[]>(initialPhieuChis);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { entity } = useAuth();

  const API_ENDPOINT = 'tien-mat/hach-toan/phieu-chi';

  const fetchPhieuChis = useCallback(async () => {
    if (!entity?.slug) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<PhieuChiListResponse>(`/entities/${entity.slug}/erp/${API_ENDPOINT}/`);
      setPhieuChis(response.data.results);
    } catch (error: any) {
      console.error('Error fetching phieu chi:', error);
      setError(error.message || 'Có lỗi xảy ra khi tải dữ liệu phiếu chi');
    } finally {
      setIsLoading(false);
    }
  }, [entity?.slug]);

  const searchPhieuChis = useCallback(
    async (searchParams: SearchFormValues) => {
      if (!entity?.slug) return;

      setIsLoading(true);
      setError(null);
      try {
        const response = await api.get<PhieuChiListResponse>(`/entities/${entity.slug}/erp/${API_ENDPOINT}/`, {
          params: searchParams
        });
        setPhieuChis(response.data.results);
      } catch (error: any) {
        console.error('Error searching phieu chi:', error);
        setError(error.message || 'Có lỗi xảy ra khi tìm kiếm phiếu chi');
      } finally {
        setIsLoading(false);
      }
    },
    [entity?.slug]
  );

  const createPhieuChi = useCallback(
    async (phieuChi: Partial<PhieuChi>): Promise<PhieuChi> => {
      if (!entity?.slug) throw new Error('Entity slug is required');

      setIsLoading(true);
      setError(null);
      try {
        const response = await api.post<PhieuChi>(`/entities/${entity.slug}/erp/${API_ENDPOINT}/`, phieuChi);
        const newPhieuChi = response.data;
        setPhieuChis(prev => [newPhieuChi, ...prev]);
        return newPhieuChi;
      } catch (error: any) {
        console.error('Error creating phieu chi:', error);
        const errorMessage = error.message || 'Có lỗi xảy ra khi tạo phiếu chi';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [entity?.slug]
  );

  const updatePhieuChi = useCallback(
    async (uuid: string, phieuChi: Partial<PhieuChi>): Promise<PhieuChi> => {
      if (!entity?.slug) throw new Error('Entity slug is required');

      setIsLoading(true);
      setError(null);
      try {
        const response = await api.put<PhieuChi>(`/entities/${entity.slug}/erp/${API_ENDPOINT}/${uuid}/`, phieuChi);
        const updatedPhieuChi = response.data;
        setPhieuChis(prev => prev.map(item => (item.uuid === uuid ? updatedPhieuChi : item)));
        return updatedPhieuChi;
      } catch (error: any) {
        console.error('Error updating phieu chi:', error);
        const errorMessage = error.message || 'Có lỗi xảy ra khi cập nhật phiếu chi';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [entity?.slug]
  );

  const deletePhieuChi = useCallback(
    async (uuid: string): Promise<boolean> => {
      if (!entity?.slug) return false;

      setIsLoading(true);
      setError(null);
      try {
        await api.delete(`/entities/${entity.slug}/erp/${API_ENDPOINT}/${uuid}/`);
        setPhieuChis(prev => prev.filter(item => item.uuid !== uuid));
        return true;
      } catch (error: any) {
        console.error('Error deleting phieu chi:', error);
        setError(error.message || 'Có lỗi xảy ra khi xóa phiếu chi');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [entity?.slug]
  );

  const getPhieuChiByUuid = useCallback(
    async (uuid: string): Promise<PhieuChi | null> => {
      if (!entity?.slug) return null;

      setIsLoading(true);
      setError(null);
      try {
        const response = await api.get<PhieuChi>(`/entities/${entity.slug}/erp/${API_ENDPOINT}/${uuid}/`);
        return response.data;
      } catch (error: any) {
        console.error('Error fetching phieu chi by uuid:', error);
        setError(error.message || 'Có lỗi xảy ra khi tải phiếu chi');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [entity?.slug]
  );

  const refreshPhieuChis = useCallback(async () => {
    await fetchPhieuChis();
  }, [fetchPhieuChis]);

  useEffect(() => {
    fetchPhieuChis();
  }, [fetchPhieuChis]);

  return {
    phieuChis,
    isLoading,
    error,
    fetchPhieuChis,
    searchPhieuChis,
    createPhieuChi,
    updatePhieuChi,
    deletePhieuChi,
    getPhieuChiByUuid,
    refreshPhieuChis
  };
};

export const fetchAllPhieuChi = async (entitySlug: string, pageSize = 200): Promise<PhieuChi[]> => {
  let page = 1;
  let all: PhieuChi[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<PhieuChiListResponse>(`/entities/${entitySlug}/erp/${QUERY_KEYS.PHIEU_CHI}/`, {
      params: {
        page,
        page_size: pageSize
      }
    });

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
